import { ELEVENLABS_TTS_MODEL } from './voiceProfile';
import { voiceApi } from '../services/api';

export const SPOKEN_TEXT_ATTRS = {
  className: 'scannable-bullet-text',
  lang: 'en-AU',
};

export const AUDIO_ELEMENT_ATTRS = {
  playsInline: true,
  preload: 'none' as const,
  muted: true,
};

export interface WebSpeechPreviewOptions {
  language?: string;
  gender?: string;
  modelId?: string;
  languageTarget?: string;
  /** Fired before each TTS chunk is requested (may take a while for long modules). */
  onPreparingAudio?: () => void;
  /** Fired when a chunk begins audible playback. */
  onPlaybackStart?: () => void;
}

function disableTextTracks(audio: HTMLMediaElement) {
  Array.from(audio.textTracks || []).forEach((track) => {
    track.mode = 'disabled';
  });
}

/** Off-DOM local audio — no DOM node, no crossOrigin, no caption tracks. */
export function createLocalAudio(src: string): HTMLAudioElement {
  const audio = new Audio(src);
  audio.preload = 'auto';
  audio.volume = 1;
  audio.controls = false;
  audio.removeAttribute('crossorigin');
  disableTextTracks(audio);
  return audio;
}

let activeElevenLabsAudio: HTMLAudioElement | null = null;
let audioPlaybackBlocked = false;

interface CachedSpeechAudio {
  key: string;
  url: string;
}

let cachedSpeechAudio: CachedSpeechAudio | null = null;
let inflightSpeechAudio: { key: string; promise: Promise<CachedSpeechAudio> } | null = null;

export function buildSpeechAudioCacheKey(
  voiceId: string,
  script: string,
  languageTarget?: string,
) {
  return `${voiceId}|${languageTarget ?? ''}|${script}`;
}

async function fetchAndCacheSpeechAudio(
  voiceId: string,
  script: string,
  languageTarget?: string,
): Promise<CachedSpeechAudio> {
  const key = buildSpeechAudioCacheKey(voiceId, script, languageTarget);
  const blob = await voiceApi.preview(voiceId, script, ELEVENLABS_TTS_MODEL, languageTarget);

  if (cachedSpeechAudio && cachedSpeechAudio.key !== key) {
    URL.revokeObjectURL(cachedSpeechAudio.url);
  }

  const url = URL.createObjectURL(blob);
  const entry = { key, url };
  cachedSpeechAudio = entry;
  return entry;
}

/** Start ElevenLabs synthesis early so playback can begin as soon as the screen is ready. */
export function prefetchSpeechAudio(
  voiceId: string,
  script: string,
  languageTarget?: string,
): Promise<CachedSpeechAudio> {
  const key = buildSpeechAudioCacheKey(voiceId, script, languageTarget);
  if (cachedSpeechAudio?.key === key) {
    return Promise.resolve(cachedSpeechAudio);
  }
  if (inflightSpeechAudio?.key === key) {
    return inflightSpeechAudio.promise;
  }

  const promise = fetchAndCacheSpeechAudio(voiceId, script, languageTarget).finally(() => {
    if (inflightSpeechAudio?.key === key) {
      inflightSpeechAudio = null;
    }
  });
  inflightSpeechAudio = { key, promise };
  return promise;
}

async function waitForAudioMetadata(audio: HTMLAudioElement): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Audio load failed'));
    };
    const cleanup = () => {
      audio.removeEventListener('loadedmetadata', onReady);
      audio.removeEventListener('error', onError);
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      resolve();
      return;
    }

    audio.addEventListener('loadedmetadata', onReady, { once: true });
    audio.addEventListener('error', onError, { once: true });
    audio.load();
  });
}

export function resetAudioPlaybackGate() {
  audioPlaybackBlocked = false;
}

/** Hard-stop all audio paths — used when ElevenLabs is unavailable. */
export function blockAllAudioPlayback() {
  audioPlaybackBlocked = true;

  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }

  if (activeElevenLabsAudio) {
    activeElevenLabsAudio.pause();
    activeElevenLabsAudio.muted = true;
    activeElevenLabsAudio.src = '';
    activeElevenLabsAudio = null;
  }

  if (typeof document !== 'undefined') {
    document.querySelectorAll('audio').forEach((node) => {
      node.pause();
      node.muted = true;
      node.removeAttribute('src');
      node.load();
    });
  }
}

export function primeAudioElement(audio: HTMLAudioElement | null) {
  if (!audio || audioPlaybackBlocked) return;

  audio.muted = false;
  audio.volume = 1;
  audio.preload = 'auto';
  audio.controls = false;
  audio.removeAttribute('crossorigin');
  disableTextTracks(audio);
}

export async function playManagedAudioBlob(blob: Blob): Promise<HTMLAudioElement> {
  if (audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  if (!blob.size) {
    throw new Error('Empty audio stream');
  }

  const blobType = blob.type || '';
  if (blobType.includes('json') || (blobType.startsWith('text/') && !blobType.includes('audio'))) {
    throw new Error('Invalid audio stream');
  }

  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  primeAudioElement(audio);

  try {
    await new Promise<void>((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Audio decode failed'));
      };
      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
      };

      if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        resolve();
        return;
      }

      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.load();
    });

    await audio.play();
    return audio;
  } catch (error) {
    URL.revokeObjectURL(url);
    audio.pause();
    audio.muted = true;
    audio.src = '';
    throw error;
  }
}

/** Native speechSynthesis is reserved for local voice preview only. */
export function speakScript(_script: string) {
  return;
}

function resolveWebSpeechLang(language?: string): string {
  switch (language) {
    case 'English (Aust)':
      return 'en-AU';
    case 'Spanish':
      return 'es-ES';
    case 'Afrikaans':
      return 'af-ZA';
    default:
      return 'en-US';
  }
}

function pickWebSpeechVoice(
  voices: SpeechSynthesisVoice[],
  language?: string,
  gender?: string,
): SpeechSynthesisVoice | undefined {
  if (!voices.length) return undefined;

  const langPrefix = resolveWebSpeechLang(language).split('-')[0];
  const langMatches = voices.filter((voice) => voice.lang.toLowerCase().startsWith(langPrefix));
  const pool = langMatches.length ? langMatches : voices;

  if (gender === 'Female') {
    return pool.find((voice) => /female|woman|zira|samantha|karen|natasha|sonia|heera/i.test(voice.name))
      ?? pool.find((voice) => !/male|david|mark|james|guy|matthew/i.test(voice.name))
      ?? pool[0];
  }

  if (gender === 'Male') {
    return pool.find((voice) => /male|man|david|mark|james|guy|matthew|george/i.test(voice.name))
      ?? pool[0];
  }

  return pool[0];
}

function loadWebSpeechVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      resolve([]);
      return;
    }

    const synth = window.speechSynthesis;
    const existing = synth.getVoices();
    if (existing.length) {
      resolve(existing);
      return;
    }

    const finish = () => {
      synth.removeEventListener('voiceschanged', finish);
      resolve(synth.getVoices());
    };

    synth.addEventListener('voiceschanged', finish);
    window.setTimeout(finish, 400);
  });
}

export async function speakPreviewWithWebSpeech(
  text: string,
  options: WebSpeechPreviewOptions = {},
): Promise<void> {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    throw new Error('Speech synthesis unavailable');
  }

  window.speechSynthesis.cancel();

  const voices = await loadWebSpeechVoices();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = resolveWebSpeechLang(options.language);
  utterance.rate = 0.95;
  utterance.pitch = options.gender === 'Female' ? 1.05 : 0.95;

  const voice = pickWebSpeechVoice(voices, options.language, options.gender);
  if (voice) utterance.voice = voice;

  await new Promise<void>((resolve, reject) => {
    utterance.onend = () => resolve();
    utterance.onerror = () => reject(new Error('Speech synthesis failed'));
    window.speechSynthesis.speak(utterance);
  });
}

async function playPreviewWithFallback(
  voiceId: string,
  text: string,
  options: WebSpeechPreviewOptions = {},
): Promise<HTMLAudioElement | null> {
  try {
    const blob = await voiceApi.preview(voiceId, text, options.modelId, options.languageTarget ?? options.language);
    const audio = await playManagedAudioBlob(blob);
    activeElevenLabsAudio = audio;

    audio.onended = () => {
      URL.revokeObjectURL(audio.src);
      audio.src = '';
      if (activeElevenLabsAudio === audio) activeElevenLabsAudio = null;
    };

    return audio;
  } catch (error) {
    throw error;
  }
}

export function cancelElevenLabsPlayback() {
  if (!activeElevenLabsAudio) return;
  activeElevenLabsAudio.pause();
  activeElevenLabsAudio.currentTime = 0;
  activeElevenLabsAudio.muted = true;
  activeElevenLabsAudio.src = '';
  activeElevenLabsAudio = null;
}

export function cancelSpeechPlayback() {
  cancelElevenLabsPlayback();
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}

function chunkSpeechText(text: string, maxLength = 1900): string[] {
  const normalized = String(text ?? '').replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];
  if (normalized.length <= maxLength) return [normalized];

  const chunks: string[] = [];
  let remaining = normalized;

  while (remaining.length > maxLength) {
    let splitAt = remaining.lastIndexOf('\n\n', maxLength);
    if (splitAt < maxLength * 0.4) splitAt = remaining.lastIndexOf('. ', maxLength);
    if (splitAt < maxLength * 0.4) splitAt = remaining.lastIndexOf(' ', maxLength);
    if (splitAt <= 0) splitAt = maxLength;

    chunks.push(remaining.slice(0, splitAt).trim());
    remaining = remaining.slice(splitAt).trim();
  }

  if (remaining) chunks.push(remaining);
  return chunks;
}

function waitForManagedAudioEnd(audio: HTMLAudioElement): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
    const settle = (next: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      next();
    };
    const onEnded = () => {
      settle(resolve);
    };
    const onError = () => {
      settle(() => reject(new Error('Playback failed')));
    };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
  });
}

/** Read long-form curriculum text in Study Partner voice (chunked for TTS limits). */
export async function speakLongFormNarration(
  text: string,
  voiceId: string,
  options: WebSpeechPreviewOptions = {},
): Promise<void> {
  const chunks = chunkSpeechText(text);
  if (!chunks.length) return;

  for (const chunk of chunks) {
    if (audioPlaybackBlocked) return;

    options.onPreparingAudio?.();

    try {
      const audio = await speakScriptWithElevenLabs(
        chunk,
        voiceId,
        options.modelId,
        options.languageTarget ?? options.language,
      );
      if (!audio) return;

      options.onPlaybackStart?.();
      await waitForManagedAudioEnd(audio);

      if (audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }
      audio.src = '';
      if (activeElevenLabsAudio === audio) activeElevenLabsAudio = null;
    } catch (error) {
      console.warn('[Study Partner narration] ElevenLabs playback failed:', error);
      break;
    }
  }
}

export async function prepareSpeechAudio(
  voiceId: string,
  script: string,
  languageTarget?: string,
) {
  if (typeof window === 'undefined' || audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  cancelElevenLabsPlayback();

  const entry = await prefetchSpeechAudio(voiceId, script, languageTarget);
  const audio = new Audio(entry.url);
  primeAudioElement(audio);

  await waitForAudioMetadata(audio);

  activeElevenLabsAudio = audio;
  audio.onended = () => {
    audio.src = '';
    if (activeElevenLabsAudio === audio) activeElevenLabsAudio = null;
  };

  return audio;
}

export async function speakScriptWithElevenLabs(
  script: string,
  voiceId: string,
  modelId?: string,
  languageTarget?: string,
) {
  if (typeof window === 'undefined' || audioPlaybackBlocked) return;

  cancelElevenLabsPlayback();

  try {
    const resolvedModelId = ELEVENLABS_TTS_MODEL;
    const blob = await voiceApi.preview(voiceId, script, resolvedModelId, languageTarget);
    const audio = await playManagedAudioBlob(blob);
    activeElevenLabsAudio = audio;
    return audio;
  } catch (error) {
    throw error;
  }
}

export async function previewVoiceAudio(
  voiceId: string,
  text: string,
  options: WebSpeechPreviewOptions = {},
) {
  if (audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  cancelElevenLabsPlayback();
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }

  return playPreviewWithFallback(voiceId, text, options);
}

export interface AudioTextSyncHandle {
  stop: () => void;
}

function sliceScriptByPlaybackProgress(script: string, progress: number) {
  if (progress >= 1) return script;
  const length = script.length;
  if (!length) return '';
  const targetIndex = Math.min(length, Math.max(0, Math.ceil(progress * length)));
  return script.slice(0, targetIndex);
}

/** @public — shared by MPL-07 and MPL-10 audio/text sync. */
export function revealTextForPlaybackProgress(script: string, progress: number) {
  return sliceScriptByPlaybackProgress(script, progress);
}

export interface AudioTextSyncOptions {
  /** When false, keep any text already on screen when playback starts. */
  clearOnPlay?: boolean;
  /** Lower bound for reveal progress (0–1), e.g. optimistic typing before audio is ready. */
  getMinProgress?: () => number;
}

/** Drive visible script text from the active HTMLAudioElement playback clock. */
export function syncTextRevealToAudioPlayback(
  audio: HTMLAudioElement,
  script: string,
  onReveal: (visibleText: string) => void,
  onComplete?: () => void,
  options?: AudioTextSyncOptions,
): AudioTextSyncHandle {
  let rafId: number | null = null;
  let stopped = false;
  let completed = false;
  let playStartedAt = 0;

  const stop = () => {
    stopped = true;
    audio.removeEventListener('play', onPlay);
    audio.removeEventListener('ended', onEnded);
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const finish = () => {
    if (completed) return;
    completed = true;
    stop();
    onReveal(script);
    onComplete?.();
  };

  const tick = () => {
    if (stopped || completed) return;

    const duration = audio.duration;
    if (duration && Number.isFinite(duration) && duration > 0) {
      const audioProgress = Math.min(1, Math.max(0, audio.currentTime / duration));
      const minProgress = Math.min(1, Math.max(0, options?.getMinProgress?.() ?? 0));
      const progress = Math.max(audioProgress, minProgress);
      onReveal(sliceScriptByPlaybackProgress(script, progress));
    } else if (playStartedAt > 0) {
      const estimatedDurationSec = Math.max(12, script.length / 14);
      const elapsedSec = (performance.now() - playStartedAt) / 1000;
      const estimatedProgress = Math.min(1, elapsedSec / estimatedDurationSec);
      const minProgress = Math.min(1, Math.max(0, options?.getMinProgress?.() ?? 0));
      const progress = Math.max(estimatedProgress, minProgress);
      onReveal(sliceScriptByPlaybackProgress(script, progress));
    } else if (options?.getMinProgress) {
      const minProgress = Math.min(1, Math.max(0, options.getMinProgress() ?? 0));
      if (minProgress > 0) {
        onReveal(sliceScriptByPlaybackProgress(script, minProgress));
      }
    }

    if (!audio.ended && !audio.paused) {
      rafId = requestAnimationFrame(tick);
    }
  };

  const onPlay = () => {
    playStartedAt = performance.now();
    if (options?.clearOnPlay !== false) {
      onReveal('');
    }
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(tick);
  };

  const onEnded = () => {
    finish();
  };

  audio.addEventListener('play', onPlay);
  audio.addEventListener('ended', onEnded);

  if (!audio.paused) {
    onPlay();
  }

  return { stop };
}

export interface OptimisticTextRevealHandle {
  stop: () => void;
  getProgress: () => number;
}

/** Reveal script text on a timer while speech audio is still loading. */
export function startOptimisticTextReveal(
  script: string,
  onReveal: (visibleText: string) => void,
  charsPerSecond = 14,
): OptimisticTextRevealHandle {
  let index = 0;
  let stopped = false;
  const tickMs = 50;
  const charsPerTick = Math.max(1, Math.round((charsPerSecond * tickMs) / 1000));

  const timer = window.setInterval(() => {
    if (stopped) return;
    index = Math.min(script.length, index + charsPerTick);
    onReveal(script.slice(0, index));
    if (index >= script.length) {
      stop();
    }
  }, tickMs);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
  };

  return {
    stop,
    getProgress: () => (script.length ? index / script.length : 0),
  };
}
